/**
 * SelectAllBanner Component
 * 
 * A reusable banner that appears when all items on the current page are selected,
 * offering the user the option to select ALL items matching their current filters.
 * 
 * Follows the Gmail-style "Select All" pattern.
 */

import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';

export default function SelectAllBanner({
  // Number of items currently selected
  selectedCount = 0,
  // Number of items on the current page
  pageCount = 0,
  // Total items matching current filters
  totalCount = 0,
  // Entity name for display ("contacts" or "deals")
  entityName = 'items',
  // Callback when user clicks to select all matching items
  onSelectAll,
  // Callback to clear all selections
  onClearSelection,
  // Whether all matching items are currently selected
  isAllSelected = false,
  // Whether the fetch is loading
  isLoading = false,
}) {
  // Theme colors
  const bannerBg = useColorModeValue('blue.50', 'blue.900');
  const bannerBorder = useColorModeValue('blue.200', 'blue.700');
  const linkColor = useColorModeValue('blue.600', 'blue.200');
  const successBg = useColorModeValue('green.50', 'green.900');
  const successBorder = useColorModeValue('green.200', 'green.700');

  // Don't show banner if nothing is selected or if page is empty
  if (selectedCount === 0 || pageCount === 0) {
    return null;
  }

  // Check if all items on current page are selected
  const allPageItemsSelected = selectedCount >= pageCount;

  // Don't show banner if not all page items are selected
  if (!allPageItemsSelected && !isAllSelected) {
    return null;
  }

  // Don't show "select all" option if total equals page count (already showing all)
  const showSelectAllOption = totalCount > pageCount && !isAllSelected;

  // All items are selected state
  if (isAllSelected) {
    return (
      <Alert
        status="success"
        variant="subtle"
        bg={successBg}
        borderWidth="1px"
        borderColor={successBorder}
        borderRadius="md"
        py={2}
        px={4}
        mb={3}
      >
        <HStack spacing={3} w="100%" justify="space-between" flexWrap="wrap">
          <HStack spacing={2}>
            <AlertIcon as={FiCheckCircle} boxSize={4} />
            <Text fontSize="sm" fontWeight="medium">
              All {totalCount.toLocaleString()} {entityName} are selected.
            </Text>
          </HStack>
          <Button
            size="sm"
            variant="link"
            color={linkColor}
            onClick={onClearSelection}
            fontWeight="semibold"
            _hover={{ textDecoration: 'underline' }}
          >
            Clear selection
          </Button>
        </HStack>
      </Alert>
    );
  }

  // Page items selected, offer to select all
  return (
    <Alert
      status="info"
      variant="subtle"
      bg={bannerBg}
      borderWidth="1px"
      borderColor={bannerBorder}
      borderRadius="md"
      py={2}
      px={4}
      mb={3}
    >
      <HStack spacing={3} w="100%" justify="space-between" flexWrap="wrap">
        <HStack spacing={2}>
          <AlertIcon boxSize={4} />
          <Text fontSize="sm">
            All {pageCount.toLocaleString()} {entityName} on this page are selected.
          </Text>
        </HStack>
        {showSelectAllOption && (
          <Button
            size="sm"
            variant="link"
            color={linkColor}
            onClick={onSelectAll}
            isLoading={isLoading}
            loadingText="Loading..."
            fontWeight="semibold"
            _hover={{ textDecoration: 'underline' }}
          >
            Select all {totalCount.toLocaleString()} {entityName}
          </Button>
        )}
      </HStack>
    </Alert>
  );
}
